import { useState, useEffect } from 'react';
import { useAfterUpdate, useUnmount } from '@betgames/bg-tools';
import { playerSettingsStore } from '@bg-shared/business/PlayerSettings';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { balanceService } from '@bg-shared/business/Balance';
import { gamesStatusEntity } from '@bg-shared/business/GamesStatus';
import { totalBetAmountEntity } from '@bg-shared/business/TotalBetAmount';
import { casinoActiveOptionsClassesEntity, casinoBetAmountsEntity } from '../store';
import { CasinoServiceFactory } from '../service/CasinoService.factory';
import { ICasinoSettings } from '../ICasinoSettings';
import { useSyncActivePayinBets } from './useSyncActivePayinBets';
import { useDefaultChipAmount } from './useDefaultChipAmount';
import { casinoBettingSelectors } from '../casinoBetting.selectors';

export const useCasinoBetting = (settings: ICasinoSettings): boolean => {
    const { apiService, validationService, chipsService } =
        CasinoServiceFactory.createServices(settings);
    const [loading, setLoading] = useState<boolean>(true);

    useDefaultChipAmount();

    const updateCurrentOptions = (): void => {
        casinoActiveOptionsClassesEntity
            .store(settings.gameId)
            .update(settings.optionsClasses || []);
    };

    useEffect(() => {
        if (!settings.disableChipsReset) {
            chipsService.reset();
        }

        let unsubscribeFromAmount: () => void = () => {};

        updateCurrentOptions();

        Promise.all([
            apiService.updateStagedBets(),
            apiService.updateLatestPayin(),
            apiService.updateLimits(),
        ]).finally(() => {
            setLoading(false);

            let previousAmount = casinoBettingSelectors.getTotalBetAmount(
                casinoBetAmountsEntity.store(settings.gameId).value,
            );

            unsubscribeFromAmount = casinoBetAmountsEntity
                .store(settings.gameId)
                .subscribe((amounts) => {
                    const newAmount = casinoBettingSelectors.getTotalBetAmount(amounts);

                    if (previousAmount !== newAmount) {
                        const amountDiff = newAmount - previousAmount;
                        totalBetAmountEntity.store(settings.gameId).increase(amountDiff);
                        previousAmount = newAmount;
                    }
                });
        });

        const unsubscribeFromStagedBets = apiService.stagedBetsResponseEvent(
            settings.stagedBetsResponseCallback,
        );

        return () => {
            unsubscribeFromStagedBets();
            unsubscribeFromAmount();
        };
    }, []);

    useEffect(() => {
        if (!partnerSettingsStore.showBalance) {
            balanceService.runEvents();
        }

        return () => {
            if (!partnerSettingsStore.showBalance) {
                balanceService.stopEvents();
            }
        };
    }, []);

    useSyncActivePayinBets(settings.gameId);

    useAfterUpdate(() => {
        if (!settings.runOpened) {
            validationService.removeInvalidAmounts();
        }
    }, [settings.runOpened]);

    useAfterUpdate(() => {
        if (!settings.disableChipsReset) {
            chipsService.reset();
        }

        updateCurrentOptions();
        apiService.updateLimits();
    }, [settings.runId, settings.roundId]);

    useUnmount(() => {
        // Clear chips betting when switching off the casino mode
        // But don't clear when navigating to another game
        if (
            !playerSettingsStore.casinoMode &&
            gamesStatusEntity.store(settings.gameId).value.openForBets
        ) {
            chipsService.clearBets();
        }
    });

    return loading;
};
